/**
 * Enhanced Tooltip Manager for Social Share Admin
 * Handles tooltip display with flexible positioning
 */
const TooltipManager = {
    activeTooltip: null,
    hoverTimer: null,

    /**
     * Show tooltip with flexible positioning
     * @param {string} text - Tooltip text
     * @param {Event} event - Mouse event
     * @param {string} position - Position: 'top', 'bottom', 'left', 'right', 'auto'
     * @param {number} delay - Delay before showing (ms)
     */
    show(text, event, position = 'top', delay = 100) {
        clearTimeout(this.hoverTimer);
        
        this.hoverTimer = setTimeout(() => {
            this.hide();
            
            const tooltip = document.createElement('div');
            tooltip.setAttribute('role', 'tooltip');
            tooltip.setAttribute('aria-live', 'polite');
            tooltip.className = 'fixed px-3 py-2 bg-slate-600 text-white text-xs rounded-lg shadow-lg whitespace-nowrap pointer-events-none transition-opacity duration-200 opacity-0';
            tooltip.style.zIndex = '999999';
            tooltip.textContent = text;
            
            // Add arrow
            const arrow = document.createElement('div');
            arrow.className = 'tooltip-arrow';
            tooltip.appendChild(arrow);
            
            document.body.appendChild(tooltip);
            
            // Calculate position
            const rect = event.target.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const finalPosition = position === 'auto' ? this.getAutoPosition(rect, tooltipRect) : position;
            
            this.positionTooltip(tooltip, arrow, rect, tooltipRect, finalPosition);
            
            this.activeTooltip = tooltip;
            
            // Fade in
            requestAnimationFrame(() => {
                if (this.activeTooltip === tooltip) {
                    tooltip.style.opacity = '1';
                }
            });
        }, delay);
    },

    /**
     * Position tooltip based on direction
     */
    positionTooltip(tooltip, arrow, targetRect, tooltipRect, position) {
        const spacing = 8; // Gap between tooltip and target
        const arrowSize = 6;
        
        // Reset transform
        tooltip.style.transform = '';
        arrow.style.cssText = '';
        
        switch(position) {
            case 'top':
                tooltip.style.left = (targetRect.left + targetRect.width / 2) + 'px';
                tooltip.style.top = (targetRect.top - tooltipRect.height - spacing) + 'px';
                tooltip.style.transform = 'translateX(-50%)';
                arrow.style.cssText = `
                    position: absolute;
                    bottom: -${arrowSize}px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: ${arrowSize}px solid transparent;
                    border-right: ${arrowSize}px solid transparent;
                    border-top: ${arrowSize}px solid #475569;
                `;
                break;
                
            case 'bottom':
                tooltip.style.left = (targetRect.left + targetRect.width / 2) + 'px';
                tooltip.style.top = (targetRect.bottom + spacing) + 'px';
                tooltip.style.transform = 'translateX(-50%)';
                arrow.style.cssText = `
                    position: absolute;
                    top: -${arrowSize}px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: ${arrowSize}px solid transparent;
                    border-right: ${arrowSize}px solid transparent;
                    border-bottom: ${arrowSize}px solid #475569;
                `;
                break;
                
            case 'left':
                tooltip.style.left = (targetRect.left - tooltipRect.width - spacing) + 'px';
                tooltip.style.top = (targetRect.top + targetRect.height / 2) + 'px';
                tooltip.style.transform = 'translateY(-50%)';
                arrow.style.cssText = `
                    position: absolute;
                    right: -${arrowSize}px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 0;
                    height: 0;
                    border-top: ${arrowSize}px solid transparent;
                    border-bottom: ${arrowSize}px solid transparent;
                    border-left: ${arrowSize}px solid #475569;
                `;
                break;
                
            case 'right':
                tooltip.style.left = (targetRect.right + spacing) + 'px';
                tooltip.style.top = (targetRect.top + targetRect.height / 2) + 'px';
                tooltip.style.transform = 'translateY(-50%)';
                arrow.style.cssText = `
                    position: absolute;
                    left: -${arrowSize}px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 0;
                    height: 0;
                    border-top: ${arrowSize}px solid transparent;
                    border-bottom: ${arrowSize}px solid transparent;
                    border-right: ${arrowSize}px solid #475569;
                `;
                break;
        }
        
        // Ensure tooltip stays within viewport
        this.keepInViewport(tooltip);
    },

    /**
     * Auto-detect best position based on available space
     */
    getAutoPosition(targetRect, tooltipRect) {
        const spacing = 8;
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        const space = {
            top: targetRect.top,
            bottom: viewport.height - targetRect.bottom,
            left: targetRect.left,
            right: viewport.width - targetRect.right
        };
        
        // Prefer top, then bottom, then right, then left
        if (space.top >= tooltipRect.height + spacing) return 'top';
        if (space.bottom >= tooltipRect.height + spacing) return 'bottom';
        if (space.right >= tooltipRect.width + spacing) return 'right';
        if (space.left >= tooltipRect.width + spacing) return 'left';
        
        // Default to top if no space
        return 'top';
    },

    /**
     * Keep tooltip within viewport bounds
     */
    keepInViewport(tooltip) {
        const rect = tooltip.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        let adjustX = 0;
        let adjustY = 0;
        
        if (rect.right > viewport.width) {
            adjustX = viewport.width - rect.right - 8;
        }
        if (rect.left < 0) {
            adjustX = -rect.left + 8;
        }
        if (rect.bottom > viewport.height) {
            adjustY = viewport.height - rect.bottom - 8;
        }
        if (rect.top < 0) {
            adjustY = -rect.top + 8;
        }
        
        if (adjustX !== 0 || adjustY !== 0) {
            const currentTransform = tooltip.style.transform;
            tooltip.style.transform = `${currentTransform} translate(${adjustX}px, ${adjustY}px)`;
        }
    },

    hide() {
        clearTimeout(this.hoverTimer);
        
        if (this.activeTooltip) {
            const tooltip = this.activeTooltip;
            tooltip.style.opacity = '0';
            
            setTimeout(() => {
                if (tooltip.parentNode && this.activeTooltip === tooltip) {
                    document.body.removeChild(tooltip);
                }
            }, 200);
            
            this.activeTooltip = null;
        }
    }
};

// Make it globally available
window.TooltipManager = TooltipManager;

// Clean up tooltips on page events
window.addEventListener('scroll', () => TooltipManager.hide());
window.addEventListener('resize', () => TooltipManager.hide());
window.addEventListener('beforeunload', () => TooltipManager.hide());